/**
 * AddBimElementsModal — add more elements to a BIM System that's already been set up.
 *
 * Opens via CustomEvent('open-add-bim-elements-modal', { detail: { systemId, systemTemplateId, systemName } }).
 *
 * The "Set Up BIM Model" wizard only runs once per system (at creation) — this covers what
 * happens afterward: the template gains a new element, or the team wants one that wasn't part
 * of the original set. Only offers element templates the system doesn't already have and that
 * are marked "Ready for use" (same safety gate as the setup wizard — drafts never land on a
 * live project by accident).
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { getBimTemplates, getBimSystemElements, addBimElements } from '../services/bim.js'

const helpStyle = {
  fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45,
}

export default function AddBimElementsModal() {
  const [open, setOpen] = useState(false)
  const [systemId, setSystemId] = useState('')
  const [systemName, setSystemName] = useState('')
  const [available, setAvailable] = useState(null)   // element templates not yet on this system, or [] / null (loading)
  const [selected, setSelected] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const { systemId: sid, systemTemplateId, systemName: sname } = e.detail || {}
      setSystemId(sid || '')
      setSystemName(sname || 'BIM System')
      setSelected({}); setError(null); setSubmitting(false)
      setAvailable(null)
      setOpen(true)
      if (!sid || !systemTemplateId) { setAvailable([]); return }
      Promise.all([getBimTemplates(), getBimSystemElements(sid)])
        .then(([systems, systemData]) => {
          const sysTmpl = (systems || []).find(s => s.id === systemTemplateId)
          const allElTmpls = (sysTmpl?.elements || []).filter(t => t.ready_for_use)
          const haveIds = new Set((systemData?.elements || []).map(el => el.element_template_id).filter(Boolean))
          setAvailable(allElTmpls.filter(t => !haveIds.has(t.id)))
        })
        .catch(() => setAvailable([]))
    }
    window.addEventListener('open-add-bim-elements-modal', onOpen)
    return () => window.removeEventListener('open-add-bim-elements-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function toggle(id) { setSelected(s => ({ ...s, [id]: !s[id] })) }
  const selectedIds = Object.keys(selected).filter(id => selected[id])

  async function handleSubmit() {
    if (!systemId || !selectedIds.length || submitting) return
    setSubmitting(true); setError(null)
    try {
      const created = await addBimElements(systemId, selectedIds)
      const key = 'bimElements:' + systemId
      const existing = ctx.loader?.get(key)
      if (Array.isArray(existing)) ctx.loader.set(key, [...existing, ...created])
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to add elements'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = selectedIds.length > 0 && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddBimElementsModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, maxHeight: '78vh', display: 'flex', flexDirection: 'column', padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexShrink: 0 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Add Elements — {systemName}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ ...helpStyle, flexShrink: 0, marginTop: 6 }}>
          Pick elements to add to this system's checklist — only elements it doesn't already have.
        </div>

        <div style={{ overflowY: 'auto', marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
          {available === null && <div style={helpStyle}>Loading…</div>}
          {available && !available.length && (
            <div style={helpStyle}>Nothing to add — every ready-for-use element from this system's template is already here.</div>
          )}
          {available && available.map(t => {
            const isSel = !!selected[t.id]
            return (
              <label key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '5px 6px', borderRadius: 4, background: isSel ? 'var(--aj-bg-subtle)' : 'none' }}>
                <input type="checkbox" checked={isSel} onChange={() => toggle(t.id)}
                  style={{ marginTop: 2, accentColor: '#1D9E75', cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>{t.name}</span>
              </label>
            )
          })}
        </div>

        {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', marginTop: 10, flexShrink: 0 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, flexShrink: 0 }}>
          <span style={helpStyle}>{selectedIds.length ? selectedIds.length + ' selected' : ''}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Adding…' : 'Add ' + (selectedIds.length || '') + ' Element' + (selectedIds.length === 1 ? '' : 's')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
